import * as React from "react";
import { observer } from "mobx-react";
import {
  UitkCard,
  UitkCardMedia,
  UitkCardLink,
  UitkCardTextSegment,
  UitkCardSectionSeparatorType,
} from "uitk-react-cards";
import { UitkFigure, UitkFigureAspectRatioType } from "uitk-react-images";
import { CampaignRecommendationsWithOffersQuery } from "../../../../../../__generated__/api";
import Offers = CampaignRecommendationsWithOffersQuery.Offers;
import { Action } from "components/utility/analytics/FlexAnalyticsUtils";
import { TrackedLink } from "components/utility/analytics/TrackedLink";
import { DestinationOfferPriceInfo } from "../DestinationOfferPriceInfo/DestinationOfferPriceInfo";
import { UitkLayoutFlex, UitkLayoutFlexItem } from "uitk-react-layout-flex";
import { destinationOfferItem } from "../../Common/MerchCampaignCard/MerchCampaignCard";
import { LazyLoadedImage } from "components/utility/LazyLoadedImage/LazyLoadedImage";

export interface ImageTopCardProps {
  offer: Offers;
  productType: string;
  cardIndex: number;
  campaignId: string;
  openDialog: (dialogPath: string) => void;
  closeDialog: () => void;
  displayDate: boolean;
  displayDescription: boolean;
  displayPrice: boolean;
  moduleName: string;
  withBorder?: boolean;
}

export const ImageTopCard = observer(
  ({
    offer,
    productType,
    cardIndex,
    campaignId,
    openDialog,
    closeDialog,
    displayDate,
    displayDescription,
    displayPrice,
    moduleName,
    withBorder,
  }: ImageTopCardProps) => {
    if (offer.__typename !== "DestinationOfferItem") {
      return null;
    }

    const offerItem = destinationOfferItem(productType, offer);

    if (offerItem === null || offerItem === undefined) {
      return null;
    }

    return (
      <>
        <UitkCard className="uitk-flex-justify-content-flex-start uitk-flex-column" withBorder={withBorder}>
          <UitkCardMedia>
            <UitkFigure ratio={UitkFigureAspectRatioType.R21_9}>
              <LazyLoadedImage src={offer.image.url} alt={offer.image.description} />
            </UitkFigure>
            <UitkCardLink>
              <TrackedLink
                moduleName={moduleName}
                action={Action.CLICK}
                href={offerItem?.actionLink.href}
                rfrr={`card-${campaignId}-${cardIndex}`}
              >
                {offer.name}
              </TrackedLink>
            </UitkCardLink>
          </UitkCardMedia>
          <div className="uitk-card-content uitk-cell">
            <UitkCardTextSegment>
              <UitkLayoutFlex justifyContent="space-between">
                <>
                  <UitkLayoutFlexItem>
                    <div>
                      <h3 className="uitk-type-heading-500">{offer.name}</h3>
                      {displayDate && <div className="uitk-type-paragraph-300">{offer.dates}</div>}
                      <div className="uitk-type-paragraph-300">{offer.secondaryContent}</div>
                    </div>
                  </UitkLayoutFlexItem>
                  <UitkLayoutFlexItem className="uitk-type-right">
                    <div>
                      {displayPrice && offerItem?.price.value && (
                        <DestinationOfferPriceInfo
                          scrimDisplay={false}
                          offerPrice={offerItem.price}
                          cardIndex={cardIndex}
                          campaignId={campaignId}
                          openDialog={openDialog}
                          closeDialog={closeDialog}
                        />
                      )}
                      {displayPrice && offerItem.price.checkPriceText && (
                        <a className="uitk-link uitk-type-300 uitk-type-bold" href={offerItem.actionLink.href}>
                          {offerItem.price.checkPriceText}
                        </a>
                      )}
                    </div>
                  </UitkLayoutFlexItem>
                </>
              </UitkLayoutFlex>
            </UitkCardTextSegment>
            {displayDescription && offer.description && (
              <UitkCardTextSegment separator={UitkCardSectionSeparatorType.TOP}>
                {offer.description && <div className="uitk-type-paragraph-300">{offer.description}</div>}
              </UitkCardTextSegment>
            )}
          </div>
        </UitkCard>
      </>
    );
  }
);
